import { computed, type ComputedRef } from 'vue';
import { useRoute } from 'vue-router';
import { usePlatform } from '@core/platform';
import { useHydrated } from '@app/composables/useHydrated';
import { useMediaQuery } from '@app/composables/useMediaQuery';
import { layoutConfig } from '@/config/layout';

export interface ResolvedLayout {
  /** Outer shell component to wrap the RouterView. */
  shell: 'site' | 'app';
  /** Chrome variant inside the app shell. */
  chrome: 'tabs' | 'bare' | 'desktop' | 'plain';
  /** Desktop-web phone-frame embed (AppShell.showDesktopFrame). */
  showDesktopFrame: boolean;
  /** Pre-hydration neutral placeholder, shown for the first client tick on a
   *  frame-eligible web route to avoid the desktop phone-frame flash (#11). */
  showFramePlaceholder: boolean;
  /** Bottom-tabs bar shown (AppShell.showBottomNav). */
  showBottomNav: boolean;
  /** Nav hidden entirely (AppNav.hideNav). */
  hideNav: boolean;
  /** Extra top padding on mobile Telegram with bottom tabs (AppShell.useMobilePadding). */
  useMobilePadding: boolean;
}

/**
 * useResolvedLayout — single source of layout truth.
 *
 * Collapses the shell/chrome branching that used to be re-derived in App.vue,
 * AppShell.vue and AppNav.vue into one place. Derivations are byte-equivalent
 * to the post-keystone AppShell/AppNav computeds (4-value `chrome` model).
 *
 * Call this ONCE from a component `setup()` (App.vue) — it uses onMounted-based
 * composables (useHydrated / useMediaQuery) and registers exactly one
 * `matchMedia` listener. App.vue passes the resolved object down to AppShell as
 * a prop so there is a single useMediaQuery instance app-wide.
 */
export function useResolvedLayout(): ComputedRef<ResolvedLayout> {
  const route = useRoute();
  const { isTelegram, isTelegramMobile } = usePlatform();
  const isHydrated = useHydrated();
  const isDesktop = useMediaQuery('(min-width: 768px)');
  const { mobileLayout } = layoutConfig;

  return computed<ResolvedLayout>(() => {
    const shell = route.meta.shell;
    const chrome = route.meta.chrome;

    // Only the phone-shaped app chrome can ever appear in the desktop phone-frame;
    // 'desktop'/'plain' routes (demos, admin, 404) are always full-width.
    const frameEligible = chrome === 'tabs' || chrome === 'bare';

    // hideNav — mirrors AppNav.vue.
    const hideNav = chrome === 'bare' || chrome === 'plain';

    // showBottomNav — the exact condition AppNav draws its fixed tab bar on
    // (bottom-tabs layout and the nav not hidden). That includes 'desktop' routes
    // (e.g. /web-demo) on a phone, where the bar still shows below md — so <main>
    // must reserve its height there too, or the last content sits under it.
    const showBottomNav = mobileLayout === 'bottom-tabs' && !hideNav;

    // showDesktopFrame — only on a hydrated, non-Telegram, desktop-web viewport.
    const showDesktopFrame = isHydrated.value && !isTelegram && isDesktop.value && frameEligible;

    // Pre-hydration flash fix (#11). Before mount, useMediaQuery is false, so we
    // can't yet tell whether a frame-eligible web route will resolve to the
    // desktop phone-frame or the mobile/full-width layout. Painting the
    // full-width layout now and snapping into the frame on desktop is the flash;
    // instead paint a neutral branded placeholder for the first tick.
    //
    // The gate is `!isHydrated` ONLY — deliberately NOT `!isDesktop`. isDesktop
    // stays false on real mobile, so gating on it would leave the placeholder up
    // forever there. isHydrated flips true on mount regardless of viewport, so
    // after mount the real branch (frame on desktop, full layout on mobile) wins
    // exactly once. Telegram never frames, so it skips the placeholder entirely.
    // Hydration-safe: no AppShell route is SSG-prerendered (those use SiteLayout).
    const showFramePlaceholder = !isHydrated.value && !isTelegram && frameEligible;

    // useMobilePadding — mirrors AppShell.vue.
    const useMobilePadding = isHydrated.value && isTelegramMobile && showBottomNav;

    return {
      shell,
      chrome,
      showDesktopFrame,
      showFramePlaceholder,
      showBottomNav,
      hideNav,
      useMobilePadding,
    };
  });
}
